import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  apiBlockUser,
  apiFollowUser,
  apiUnblockUser,
  apiUnfollowUser,
} from '#/api/users';
import { Avatar } from '#/components/Avatar/Avatar';
import { Button } from '#/components/Button/Button';
import { Divider } from '#/components/Divider/Divider';
import { Stack } from '#/components/Stack/Stack';
import { Text } from '#/components/Text/Text';
import { UnfollowButton } from '#/components/UnfollowButton/UnfollowButton';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useKeyedState } from '#/hooks/useKeyedState';
import { useI18n } from '#/i18n/hooks/useI18n';
import { userDashboardApiEnableProfileFeatures } from '#/modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import type { ApiUserProfile } from '#/modules/userProfile/api/types/ApiUserProfile';
import { ViewUserProfilePageBannerImage } from '#/modules/userProfile/components/ViewUserProfilePageBannerImage';
import { ViewUserProfilePageDropdown } from '#/modules/userProfile/components/ViewUserProfilePageDropdown';
import { ViewUserProfilePageContentSectionDetails } from '#/modules/userProfile/components/sections/ViewUserProfilePageContentSectionDetails';
import { ViewUserProfilePageContentSectionProfessionalExperience } from '#/modules/userProfile/components/sections/ViewUserProfilePageContentSectionProfessionalExperience';
import { ViewUserProfilePageContentSectionSummary } from '#/modules/userProfile/components/sections/ViewUserProfilePageContentSectionSummary';
import { fontWeights, legacySpace } from '#/theme/theme';
import { showModal } from '#/zustand-stores/modalStore';
import {
  setPreSignupFollowUserId,
  usePreSignupFollow,
} from '#/zustand-stores/preSignupFollowStore';

import {
  ViewUserProfileContentActionButtons,
  ViewUserProfileContentActionButtonsContainer,
  ViewUserProfilePageBlockedBanner,
  ViewUserProfilePageContentCenter,
  ViewUserProfilePageContentContainer,
  ViewUserProfilePageContentSidebar,
  ViewUserProfilePageContentSidebarFollow,
  ViewUserProfilePageContentSidebarLocation,
  ViewUserProfilePageContentSidebarTitle,
  ViewUserProfilePageContentSidebarUsername,
  ViewUserProfilePageCoverHolder,
  ViewUserProfilePageCoverHolderBlur,
  ViewUserProfilePageCoverHolderForeground,
} from './ViewUserProfilePage.styled';

type Props = {
  initialUserProfile: ApiUserProfile;
};

export function ViewUserProfilePagePublicProfile({
  initialUserProfile,
}: Props) {
  const [userProfile, setUserProfile] = useKeyedState(initialUserProfile, {
    key: initialUserProfile.id,
  });

  const { i18n } = useI18n();

  const { user } = useUser();

  const { preSignupFollowUserId } = usePreSignupFollow();

  const [unfinishedFollow, setUnfinishedFollow] = useState<boolean | null>(
    null,
  );

  const handleFollowUnauthenticated = useCallback(() => {
    showModal('EMAIL_SIGNUP', {
      title: i18n('Sign up to follow this user'),
      onSignup: () => {
        setPreSignupFollowUserId(userProfile.id);
      },
      onLogin: () => {
        setPreSignupFollowUserId(userProfile.id);
      },
      requirePassword: true,
    });
  }, [i18n, userProfile]);

  const handleFollowUnauthorized = useCallback(() => {
    if (!user) return;

    if (!user.hasIdealistProfileFeature) {
      userDashboardApiEnableProfileFeatures().then(() => {
        showModal('CREATE_USER_PROFILE', {});
        setUnfinishedFollow(true);
      });
      return;
    }

    if (!user.hasPublishedIdealistProfile) {
      showModal('CREATE_USER_PROFILE', {});
      setUnfinishedFollow(true);
    }
  }, [user, setUnfinishedFollow]);

  const handleFollow = useCallback(
    async () =>
      apiFollowUser(userProfile.username).then(() => {
        setUserProfile({ ...userProfile, followed: true });
      }),
    [setUserProfile, userProfile],
  );

  const handleUnfollow = useCallback(
    async () =>
      apiUnfollowUser(userProfile.username).then(() => {
        setUserProfile({ ...userProfile, followed: false });
      }),
    [setUserProfile, userProfile],
  );

  const handleBlock = useCallback(
    () =>
      apiBlockUser(userProfile?.username).then(() => {
        setUserProfile({ ...userProfile, blocked: true });
      }),
    [setUserProfile, userProfile],
  );

  const handleUnblock = useCallback(
    () =>
      apiUnblockUser(userProfile?.username).then(() => {
        setUserProfile({ ...userProfile, blocked: false });
      }),
    [setUserProfile, userProfile],
  );

  const handleFlag = useCallback(() => {
    showModal('FLAG_LISTING', {
      listingId: userProfile.id,
      listingType: 'USER',
      userType: 'PROFILE',
    });
  }, [userProfile]);

  const hasDetails = useMemo(
    () =>
      Boolean(
        userProfile &&
          (userProfile?.areasOfFocus?.length > 0 ||
            userProfile?.links?.length > 0),
      ),
    [userProfile],
  );

  const hasProfessionalExperience = useMemo(
    () =>
      Boolean(
        userProfile?.applicantProfile &&
          userProfile?.applicantProfile.workHistoryJobs.length > 0,
      ),
    [userProfile],
  );
  const canBlock = useMemo(
    () =>
      Boolean(
        user &&
          userProfile &&
          !userProfile.blocked &&
          userProfile.id !== user.id,
      ),
    [user, userProfile],
  );

  const canFlag = useMemo(
    () => Boolean(!user || (userProfile && userProfile.id !== user.id)),
    [user, userProfile],
  );

  const canFollowUnauthenticated = useMemo(
    () =>
      Boolean(
        !user && userProfile && !userProfile.followed && !userProfile.blocked,
      ),
    [user, userProfile],
  );

  const canFollowUnauthorized = useMemo(
    () =>
      Boolean(
        user &&
          userProfile &&
          !userProfile.followed &&
          !userProfile.blocked &&
          userProfile.id !== user.id &&
          !user.hasPublishedIdealistProfile,
      ),
    [user, userProfile],
  );

  const canFollow = useMemo(
    () =>
      Boolean(
        user &&
          userProfile &&
          !userProfile.followed &&
          !userProfile.blocked &&
          userProfile.id !== user.id &&
          user.hasPublishedIdealistProfile,
      ),
    [user, userProfile],
  );

  const canUnfollow = useMemo(
    () =>
      Boolean(
        user &&
          userProfile &&
          userProfile.followed &&
          userProfile.id !== user.id &&
          user.hasPublishedIdealistProfile,
      ),
    [user, userProfile],
  );

  useEffect(() => {
    if (
      user?.emailVerified &&
      preSignupFollowUserId &&
      preSignupFollowUserId === userProfile.id
    ) {
      setUnfinishedFollow(true);
    }
  }, [userProfile, preSignupFollowUserId, user?.emailVerified]);

  useEffect(() => {
    if (unfinishedFollow && userProfile && user?.hasPublishedIdealistProfile) {
      apiFollowUser(userProfile.username).then(() => {
        setUserProfile({ ...userProfile, followed: true });
      });

      setPreSignupFollowUserId(null);
      setUnfinishedFollow(null);
    }
  }, [
    unfinishedFollow,
    user?.hasPublishedIdealistProfile,
    userProfile,
    setUserProfile,
    handleFollow,
  ]);

  return (
    <>
      <ViewUserProfilePageCoverHolder>
        <ViewUserProfilePageCoverHolderBlur>
          <ViewUserProfilePageBannerImage data={userProfile.bannerImage} />
        </ViewUserProfilePageCoverHolderBlur>

        <ViewUserProfilePageCoverHolderForeground>
          <ViewUserProfilePageBannerImage
            data={userProfile.bannerImage}
            qaId={
              userProfile.bannerImage
                ? 'view-user-profile-page-banner-image'
                : undefined
            }
          />
        </ViewUserProfilePageCoverHolderForeground>
      </ViewUserProfilePageCoverHolder>

      <ViewUserProfilePageContentContainer>
        <ViewUserProfilePageContentSidebar>
          <Avatar
            image={userProfile.image}
            name={userProfile.name}
            size="xl"
            border="white"
            eagerLoad
            imageQaId="view-user-profile-page-image"
          />

          <ViewUserProfilePageContentSidebarTitle data-qa-id="view-user-profile-page-name">
            {userProfile.name}
          </ViewUserProfilePageContentSidebarTitle>

          <ViewUserProfilePageContentSidebarLocation data-qa-id="view-user-profile-page-location">
            {userProfile.location}
          </ViewUserProfilePageContentSidebarLocation>

          <ViewUserProfilePageContentSidebarUsername data-qa-id="view-user-profile-page-username">
            @{userProfile.username}
          </ViewUserProfilePageContentSidebarUsername>

          {(canFollow || canFollowUnauthenticated || canFollowUnauthorized) && (
            <ViewUserProfilePageContentSidebarFollow>
              <Button
                type="button"
                onClick={() => {
                  if (canFollow) return handleFollow();
                  if (canFollowUnauthenticated)
                    return handleFollowUnauthenticated();
                  if (canFollowUnauthorized) return handleFollowUnauthorized();
                }}
                variant="primary"
                qaId="follow"
                icon={{
                  type: 'normalized',
                  name: 'plus',
                  color: 'white',
                  size: 16,
                }}
                iconPosition="left"
              >
                {i18n('Follow')}
              </Button>
            </ViewUserProfilePageContentSidebarFollow>
          )}

          {canUnfollow && (
            <ViewUserProfilePageContentSidebarFollow>
              <UnfollowButton
                username={userProfile.username}
                onClick={handleUnfollow}
              />
            </ViewUserProfilePageContentSidebarFollow>
          )}
        </ViewUserProfilePageContentSidebar>

        <ViewUserProfilePageContentCenter>
          {userProfile.blocked && (
            <ViewUserProfilePageBlockedBanner>
              <Text mb={legacySpace[3]} fontWeight={fontWeights.semiBold}>
                {i18n('You have blocked this user')}
              </Text>

              <Button
                type="button"
                variant="danger"
                qaId="unblock"
                onClick={handleUnblock}
              >
                {i18n('Unblock')}
              </Button>
            </ViewUserProfilePageBlockedBanner>
          )}

          {!userProfile.blocked && (
            <>
              {userProfile.id !== user?.id && (
                <>
                  <ViewUserProfileContentActionButtonsContainer>
                    <ViewUserProfileContentActionButtons>
                      <ViewUserProfilePageDropdown
                        showBlock={canBlock}
                        showFlag={canFlag}
                        handleBlock={handleBlock}
                        handleFlag={handleFlag}
                      />
                    </ViewUserProfileContentActionButtons>
                  </ViewUserProfileContentActionButtonsContainer>
                  <Divider marginTop={15} marginBottom={30} />
                </>
              )}

              <Stack
                direction="column"
                divider={<Divider marginTop={30} marginBottom={30} />}
              >
                <ViewUserProfilePageContentSectionSummary
                  summary={userProfile.summary}
                />
                {hasDetails ? (
                  <ViewUserProfilePageContentSectionDetails
                    areasOfFocus={userProfile.areasOfFocus}
                    links={userProfile.links}
                  />
                ) : null}
                {hasProfessionalExperience ? (
                  <ViewUserProfilePageContentSectionProfessionalExperience
                    applicantProfile={userProfile.applicantProfile}
                  />
                ) : null}
              </Stack>
            </>
          )}
        </ViewUserProfilePageContentCenter>
      </ViewUserProfilePageContentContainer>
    </>
  );
}
